import { getSortParam } from '@disco/api/helpers'
import { PLAYLIST, TRACK } from '@disco/constants/ItemTypes'
import type { SORT } from '@disco/constants/SortTypes'
import {
  GROUP_PANE,
  PLAYLIST_PANE,
  SEARCH_RESULTS_PANE,
  TRACK_PANE,
} from '@disco/constants/PaneTypes'
import { getPaneSortSettingsWithDefaults } from '@disco/utils/sorting'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import { createSelector } from 'reselect'
import type {
  ExplorerPane,
  ExplorerPaneType,
  ExplorerUpload,
} from 'types/explorer'
import type { GroupId } from 'types/group'
import type { State } from 'types/redux'
import type { TrackId } from 'types/track'
import { getGroup } from '@disco/state-management/selectors/groups'
import { getTracks } from '@disco/state-management/selectors/tracks'
import {
  getNumberOfTracks,
  getUserSettings,
} from '@disco/state-management/selectors/user'
/**
 * Gets the explorer state.
 * @param state - The Redux state
 * @returns The explorer state object
 */
export const getExplorer = (state: State): State['explorer'] => state.explorer
/**
 * Gets all explorer panes.
 * @param state - The Redux state
 * @returns Array of explorer pane objects
 */
export const getExplorerPanes = (state: any): ExplorerPane[] =>
  getExplorer(state).panes
/**
 * Gets the first explorer pane.
 * @param state - The Redux state
 * @returns The first explorer pane, or null if no panes exist
 */
export const getExplorerFirstPane = (
  state: State,
): ExplorerPane | null | undefined => getExplorer(state).panes[0] ?? null
/**
 * Checks if the user has any tracks (business tracks or local tracks).
 * @param state - The Redux state
 * @returns True if the user has tracks, false otherwise
 */
export const getHasTracks = (state: State): boolean => {
  const businessTracks = getNumberOfTracks(state)
  const tracks = getTracks(state)
  const trackCount = Object.keys(tracks).length
  return businessTracks + trackCount > 0
}
/**
 * Gets the type of the currently active explorer pane.
 * @param state - The Redux state
 * @returns The active pane type, or null if no active pane is loaded
 */
export const getExplorerActivePaneType = (
  state: State,
): (ExplorerPaneType | typeof GROUP_PANE) | null | undefined => {
  const pane = state.explorer.panes[state.explorer.panes.length - 1]
  if (!pane?.loaded) return null
  const type = pane?.type

  if (pane?.groupId && type === SEARCH_RESULTS_PANE) {
    return GROUP_PANE
  }

  return type
}
/**
 * Gets the group ID of the first explorer pane.
 * @param state - The Redux state
 * @returns The group ID of the first pane, or null if no group ID
 */
export const getExplorerFirstPaneGroupId = (
  state: State,
): GroupId | null | undefined => get(state.explorer, 'panes[0].groupId', null)
/**
 * Gets the number of explorer panes.
 * @param state - The Redux state
 * @returns The count of explorer panes
 */
export const getExplorerPanesCount = (state: State): number =>
  state.explorer.panes.length
/**
 * Gets all track IDs from all explorer panes.
 * @param state - The Redux state
 * @returns Array of track IDs from search results and playlist panes
 */
export const getExplorerTrackIds = createSelector(
  [getExplorerPanes],
  (panes: ExplorerPane[]) => {
    const results: number[] = []
    panes.forEach((pane: ExplorerPane) => {
      if (pane.type === SEARCH_RESULTS_PANE) {
        results.push(
          ...(pane.results ?? [])
            .filter(item => item.type === TRACK)
            .map(item => item.id),
        )
      }

      if (pane.type === PLAYLIST_PANE && pane.tracks) {
        results.push(...pane.tracks)
      }
    })
    return results
  },
)
/**
 * Gets all playlist IDs from all explorer panes.
 * @param state - The Redux state
 * @returns Array of playlist IDs from search results and track panes
 */
export const getExplorerPlaylistIds = createSelector(
  [getExplorerPanes],
  panes => {
    const results: any[] = []
    panes.forEach((pane: ExplorerPane) => {
      if (pane.type === SEARCH_RESULTS_PANE) {
        results.push(
          ...(pane.results ?? [])
            .filter(item => item.type === PLAYLIST)
            .map((item: any) => item.playlist_id),
        )
      }

      if (pane.type === TRACK_PANE && pane.playlists) {
        results.push(...pane.playlists)
      }
    })
    return results
  },
)
/**
 * Gets the sort settings for group shares based on user settings and pane overrides.
 * @param state - The Redux state
 * @param groupId - The group ID to get sort settings for
 * @returns Sort parameter string for the group
 */
export const getGroupShareSortSettings = createSelector(
  [getUserSettings, getGroup, getExplorerPanes],
  (settings, group, panes) => {
    let { playlistSortDirection, playlistSortParam } = group
      ? getPaneSortSettingsWithDefaults(
          {
            groupId: group.id,
            groupType: group.group_type,
          },
          settings,
        )
      : {}
    const pane = group
      ? panes.find(
          p => p.groupId === group.id && p.type === SEARCH_RESULTS_PANE,
        )
      : null

    if (pane && pane.playlistSortDirection && pane.playlistSortParam) {
      playlistSortDirection = pane.playlistSortDirection
      playlistSortParam = pane.playlistSortParam
    }

    const sortParam = getSortParam(
      group?.group_type,
      // getPaneSortSettingsWithDefaults always returns a defined SORT when group is present
      playlistSortParam as SORT,
      playlistSortDirection,
    )
    return sortParam
  },
)
/**
 * Gets explorer panes that contain a specific track ID.
 * @param state - The Redux state
 * @param trackId - The track ID to search for
 * @returns Array of explorer panes containing the track
 */
export const getExplorerPanesByTrackId = (
  state: State,
  trackId: TrackId,
): ExplorerPane[] => {
  return state.explorer.panes?.filter(pane => pane.id === trackId)
}
/**
 * Gets the number of files currently uploading to explorer.
 * @param state - The Redux state
 * @returns The count of uploading files
 */
export const getUploadingToExplorerFilesCount = (state: State): number =>
  getExplorer(state).uploads.length

/**
 * Gets a tracked explorer upload by its temp id.
 * @param state - The Redux state
 * @param tempId - Temp id of the upload
 * @returns The tracked upload, or undefined once it has been removed
 */
export const getExplorerUpload = (
  state: State,
  tempId: string,
): ExplorerUpload | undefined =>
  getExplorer(state).uploads.find(upload => upload.tempId === tempId)

/**
 * Factory selector for the temp ids of uploads targeting a group.
 *
 * Returns a referentially stable array while the id list is unchanged, so a
 * pane subscribing to it re-renders on upload add/remove but not on every
 * per-file progress tick. Create one instance per connected pane.
 */
export const makeGetPaneUploadTempIds = () =>
  createSelector(
    [
      (state: State) => getExplorer(state).uploads,
      (_state: State, groupId: GroupId | null | undefined) => groupId,
    ],
    (uploads, groupId) =>
      uploads
        .filter(upload => upload.groupId !== null && upload.groupId === groupId)
        .map(upload => upload.tempId),
    { memoizeOptions: { resultEqualityCheck: isEqual } },
  )
/**
 * Gets a specific explorer pane by index.
 * @param state - The Redux state
 * @param props - Object containing the pane index
 * @param props.index - The index of the pane to retrieve
 * @returns The explorer pane at the specified index, or null if index is out of bounds
 */
export const getExplorerPane = (
  state: State,
  {
    index,
  }: {
    index: number
  },
): ExplorerPane | null | undefined => {
  const panes = getExplorerPanes(state)
  return panes.length > index ? panes[index] : null
}
